import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Play, Square, Coffee, Timer, Calendar, TrendingUp, Download, Plus, Edit2, Trash2, UserCircle, X, Search, Users, LogIn, LogOut, Filter } from "lucide-react";
import * as XLSX from 'xlsx';
import { API_URL } from '../config';
import { pauseNoteFor } from '../utils/pauseNote';

// ─── Couleurs par rôle ──────────────────────────────────────────────────────
const ROLE_COLORS = {
  'Patron':        { bg: 'bg-red-100 dark:bg-red-900/40',    text: 'text-red-700 dark:text-red-300',    avatar: 'from-red-400 to-red-600' },
  'Super Manager': { bg: 'bg-violet-100 dark:bg-violet-900/40', text: 'text-violet-700 dark:text-violet-300', avatar: 'from-violet-400 to-violet-600' },
  'Manager':       { bg: 'bg-blue-100 dark:bg-blue-900/40',   text: 'text-blue-700 dark:text-blue-300',   avatar: 'from-blue-400 to-blue-600' },
  'Employé':       { bg: 'bg-green-100 dark:bg-green-900/40',  text: 'text-green-700 dark:text-green-300',  avatar: 'from-green-400 to-green-600' },
  'Extra':         { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300', avatar: 'from-orange-400 to-orange-600' },
};

// Encadré recap modulation mensuelle
function ModulationRecapCard({ activeUser, monthlyStats }) {
  const heuresHebdo = activeUser?.heures_hebdomadaires || 35;
  const mapping = { 35: 151.67, 39: 169.00, 40: 173.33 };
  const heuresContrat = mapping[heuresHebdo] || 151.67;
  const heuresPointees = parseFloat(monthlyStats.hours) || 0;
  const deltaMois = heuresPointees - heuresContrat;
  const compteur = (activeUser?.compteur_modulation || 0) + deltaMois;
  const isPositive = compteur >= 0;

  return (
    <div className={`bg-surface rounded-xl shadow-lg overflow-hidden border-l-4 ${isPositive ? 'border-green-500 dark:border-green-500' : 'border-red-500 dark:border-red-500'}`}>
      <div className="px-4 sm:px-6 py-4">
        <h3 className="text-sm font-bold text-text-primary mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-text-secondary" />
          Modulation — {new Date().toLocaleString('fr-FR', { month: 'long', year: 'numeric' })}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <p className="text-xs text-text-muted">Heures contrat</p>
            <p className="text-sm font-semibold text-text-secondary">{heuresContrat.toFixed(1)}h</p>
          </div>
          <div>
            <p className="text-xs text-text-muted">Heures pointées</p>
            <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">{heuresPointees.toFixed(2)}h</p>
          </div>
          <div>
            <p className="text-xs text-text-muted">Delta ce mois</p>
            <p className={`text-sm font-semibold ${deltaMois >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {deltaMois >= 0 ? '+' : ''}{deltaMois.toFixed(2)}h
            </p>
          </div>
          <div>
            <p className="text-xs text-text-muted">Solde cumulé</p>
            <p className={`text-lg font-bold ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {compteur >= 0 ? '+' : ''}{compteur.toFixed(2)}h
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PointeuseAvancee({
  currentUser,
  pointages = [],
  onUpdate,
  employees = [],
  authToken,
  authUser,
  selectedEmployeeId,
  setSelectedEmployeeId
}) {
  const [loading, setLoading] = useState(false);
  const [currentPointage, setCurrentPointage] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weeklyStats, setWeeklyStats] = useState({ hours: 0, days: 0 });
  const [monthlyStats, setMonthlyStats] = useState({
    hours: 0,
    days: 0,
    expectedHours: 0,
    remainingHours: 0,
    extraHours: 0,
    avgPerWeek: 0
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPointage, setEditingPointage] = useState(null);
  // États filtres sélecteur profil
  const [searchFilter, setSearchFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showExtras, setShowExtras] = useState(false);
  const [showPtgFilters, setShowPtgFilters] = useState(false);
  const [ptgViewPage, setPtgViewPage] = useState(1);
  const PTG_PER_PAGE = 10;
  const [ptgMonthFilter, setPtgMonthFilter] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [pointageForm, setPointageForm] = useState({
    date: new Date().toISOString().split('T')[0],
    heure_arrivee: '',
    heure_depart: '',
    pause_debut: '',
    pause_fin: '',
    notes: ''
  });

  // ==================== GESTION PROFIL SÉLECTIONNÉ ====================
  // Calculer l'utilisateur actif et son ID (employé sélectionné ou currentUser)
  const activeUser = selectedEmployeeId
    ? employees.find(e => e.id === parseInt(selectedEmployeeId))
    : currentUser;

  const activeUserId = selectedEmployeeId
    ? parseInt(selectedEmployeeId)
    : currentUser?.employee_id;

  // ==================== INITIALISATION EMPLOYÉ SÉLECTIONNÉ ====================
  // Initialiser selectedEmployeeId avec currentUser si pas déjà défini
  useEffect(() => {
    if (currentUser && !selectedEmployeeId) {
      setSelectedEmployeeId(currentUser.employee_id || currentUser.id);
    }
  }, [currentUser, selectedEmployeeId, setSelectedEmployeeId]);

  // ==================== TIMER TEMPS RÉEL ====================
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // ==================== DÉTECTION POINTAGE EN COURS ====================
  useEffect(() => {
    if (!activeUserId) {
      setCurrentPointage(null);
      return;
    }

    // Filtre défensif : on ignore les pointages "en_cours" mal formés (check_in/date manquants)
    // — héritage potentiel d'anciennes versions ou de transitions de rôle (ex: Extra → Employé)
    // qui peuvent laisser des zombies bloquant l'UI (impossible de pointer pause/départ).
    const activePointage = pointages
      .filter(p => p.user_id === activeUserId
        && p.statut === 'en_cours'
        && p.heure_arrivee
        && p.date)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null;

    setCurrentPointage(activePointage);
  }, [pointages, activeUserId, selectedEmployeeId]);

  // ==================== CALCUL STATISTIQUES ====================
  useEffect(() => {
    if (!activeUserId || !pointages.length || !activeUser) return;

    const now = new Date();
    const userPointages = pointages.filter(p => p.user_id === activeUserId && p.statut === 'termine');

    // Fonction pour obtenir la date au format YYYY-MM-DD sans conversion UTC
    const getDateStr = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Calcul semaine calendaire en cours (du lundi au dimanche)
    const weekStart = new Date(now);
    const dayOfWeek = now.getDay(); // 0 = dimanche, 1 = lundi, ..., 6 = samedi
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Si dimanche, reculer de 6 jours, sinon reculer jusqu'au lundi
    weekStart.setDate(now.getDate() + daysToMonday);
    weekStart.setHours(0, 0, 0, 0); // Début de la journée du lundi

    // Convertir en format YYYY-MM-DD sans décalage UTC
    const weekStartStr = getDateStr(weekStart);
    const todayStr = getDateStr(now);

    const weekPointages = userPointages.filter(p => {
      return p.date >= weekStartStr && p.date <= todayStr;
    });

    const weeklyHours = weekPointages.reduce((sum, p) => sum + (parseFloat(p.heures_travaillees) || 0), 0);
    const weeklyDays = new Set(weekPointages.map(p => p.date)).size;

    setWeeklyStats({
      hours: weeklyHours.toFixed(2),
      days: weeklyDays
    });

    // Calcul mois en cours
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const monthPointages = userPointages.filter(p => {
      const pDate = new Date(p.date);
      return pDate >= monthStart && pDate <= now;
    });

    const monthlyHours = monthPointages.reduce((sum, p) => sum + (parseFloat(p.heures_travaillees) || 0), 0);
    const monthlyDays = new Set(monthPointages.map(p => p.date)).size;

    // Calculer les heures attendues selon le contrat (standard français)
    const heuresHebdo = activeUser.heures_hebdomadaires || (activeUser.role_name === 'Extra' ? 0 : 35);

    // Heures mensuelles standard selon le contrat français :
    // 35h/semaine = 151,67h/mois (35 * 52 / 12)
    // 39h/semaine = 169h/mois (39 * 52 / 12)
    // Formule générale : heures_hebdo * 52 / 12
    const heuresMensuelles = (heuresHebdo * 52) / 12;

    // Calculer la proportion du mois écoulée
    const totalDaysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const currentDayOfMonth = now.getDate();
    const proportionMoisEcoule = currentDayOfMonth / totalDaysInMonth;

    // Heures attendues = heures mensuelles * proportion du mois écoulée
    const expectedHours = heuresMensuelles * proportionMoisEcoule;

    // Heures restantes = heures mensuelles totales - heures travaillées
    const remainingHours = heuresMensuelles - monthlyHours;

    // Heures d'avance/retard par rapport au prorata temporis attendu
    const extraHours = monthlyHours - expectedHours;

    // Moyenne par semaine (en utilisant le nombre réel de semaines dans le mois)
    const weeksInMonth = heuresMensuelles / heuresHebdo;
    const avgPerWeek = weeksInMonth > 0 ? monthlyHours / weeksInMonth : 0;

    setMonthlyStats({
      hours: monthlyHours.toFixed(2),
      days: monthlyDays,
      expectedHours: expectedHours.toFixed(2),
      remainingHours: remainingHours.toFixed(2),
      extraHours: extraHours.toFixed(2),
      avgPerWeek: avgPerWeek.toFixed(2)
    });

  }, [pointages, activeUserId, selectedEmployeeId, activeUser]);

  // ==================== CALCUL TEMPS ÉCOULÉ ====================
  const getElapsedTime = (pointage) => {
    if (!pointage || !pointage.heure_arrivee) return null;

    const [year, month, day] = pointage.date.split('-').map(Number);
    const [hour, minute] = pointage.heure_arrivee.split(':').map(Number);
    const startTime = new Date(year, month - 1, day, hour, minute);
    
    // Soustraire le temps de pause si applicable
    let pauseDuration = 0;
    if (pointage.pause_debut && pointage.pause_fin) {
      const [pStartH, pStartM] = pointage.pause_debut.split(':').map(Number);
      const [pEndH, pEndM] = pointage.pause_fin.split(':').map(Number);
      const pauseStart = new Date(year, month - 1, day, pStartH, pStartM);
      const pauseEnd = new Date(year, month - 1, day, pEndH, pEndM);
      pauseDuration = pauseEnd - pauseStart;
    }

    const elapsed = currentTime.getTime() - startTime.getTime() - pauseDuration;
    const hours = Math.floor(elapsed / (1000 * 60 * 60));
    const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((elapsed % (1000 * 60)) / 1000);

    return { hours, minutes, seconds, total: elapsed };
  };

  // ==================== CALCUL TEMPS PAUSE ====================
  const getCurrentPauseTime = (pointage) => {
    if (!pointage || !pointage.pause_debut || pointage.pause_fin) return null;

    const [year, month, day] = pointage.date.split('-').map(Number);
    const [hour, minute] = pointage.pause_debut.split(':').map(Number);
    const pauseStartTime = new Date(year, month - 1, day, hour, minute);
    
    const elapsed = currentTime.getTime() - pauseStartTime.getTime();
    const minutes = Math.floor(elapsed / (1000 * 60));
    const seconds = Math.floor((elapsed % (1000 * 60)) / 1000);

    return { minutes, seconds };
  };

  // ==================== ACTIONS POINTAGE ====================
  const handleArrivee = async () => {
    if (!activeUserId) return;

    setLoading(true);
    try {
      const now = new Date();
      const heureActuelle = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

      // Date locale (pas UTC) pour rester cohérent avec heureActuelle qui utilise getHours()
      const dateLocale = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      const response = await fetch(`${API_URL}/pointages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({
          user_id: activeUserId,
          date: dateLocale,
          heure_arrivee: heureActuelle,
          statut: 'en_cours',
          notes: 'Pointage depuis l\'application'
        })
      });

      if (!response.ok) throw new Error('Erreur lors du pointage');

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erreur pointage arrivée:", error);
      alert("Erreur lors du pointage d'arrivée");
    } finally {
      setLoading(false);
    }
  };

  const handleDepart = async () => {
    if (!currentPointage || !activeUserId) return;

    setLoading(true);
    try {
      const now = new Date();
      const heureActuelle = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

      // Le backend est la SOURCE DE VÉRITÉ : il recalcule total_hours + le détail de pause
      // (45 min / 1 h formation / aucune si événement) en croisant avec le planning du jour.
      // On n'envoie donc plus heures_travaillees.
      const response = await fetch(`${API_URL}/pointages/${currentPointage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({
          heure_depart: heureActuelle,
          statut: 'termine'
        })
      });

      if (!response.ok) throw new Error('Erreur lors du pointage de départ');

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erreur pointage départ:", error);
      alert("Erreur lors du pointage de départ");
    } finally {
      setLoading(false);
    }
  };

  const handlePauseDebut = async () => {
    if (!currentPointage) return;

    setLoading(true);
    try {
      const now = new Date();
      const heureActuelle = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

      const response = await fetch(`${API_URL}/pointages/${currentPointage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({
          pause_debut: heureActuelle
        })
      });

      if (!response.ok) throw new Error('Erreur début pause');

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erreur pause:", error);
      alert("Erreur lors du début de pause");
    } finally {
      setLoading(false);
    }
  };

  const handlePauseFin = async () => {
    if (!currentPointage) return;

    setLoading(true);
    try {
      const now = new Date();
      const heureActuelle = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

      const response = await fetch(`${API_URL}/pointages/${currentPointage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({
          pause_fin: heureActuelle
        })
      });

      if (!response.ok) throw new Error('Erreur fin pause');

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erreur fin pause:", error);
      alert("Erreur lors de la fin de pause");
    } finally {
      setLoading(false);
    }
  };

  // ==================== CONTRÔLE D'ACCÈS ====================
  const canManagePointages = () => {
    return authUser && ['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name);
  };

  const canExportExcel = () => {
    // Tous les utilisateurs peuvent exporter leurs heures
    return authUser && ['Patron', 'Super Manager', 'Manager', 'Employé', 'Extra'].includes(authUser.role_name);
  };

  // ==================== EXPORT EXCEL ====================
  const handleExportExcel = () => {
    if (!canExportExcel()) return;

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    // Filtrer les pointages du mois
    const targetUserId = selectedEmployeeId ? parseInt(selectedEmployeeId) : currentUser.employee_id;
    const monthPointages = pointages.filter(p => {
      const pDate = new Date(p.date);
      return p.user_id === targetUserId &&
             pDate >= monthStart &&
             pDate <= monthEnd &&
             p.statut === 'termine';
    }).sort((a, b) => new Date(a.date) - new Date(b.date));

    if (monthPointages.length === 0) {
      alert('Aucun pointage à exporter pour ce mois');
      return;
    }

    // Préparer les données pour Excel
    const excelData = monthPointages.map(p => ({
      'Date': new Date(p.date).toLocaleDateString('fr-FR'),
      'Arrivée': p.heure_arrivee || '',
      'Départ': p.heure_depart || '',
      'Pause Début': p.pause_debut || '',
      'Pause Fin': p.pause_fin || '',
      'Heures Travaillées': p.heures_travaillees || 0,
      'Note (pause)': pauseNoteFor(p)
    }));

    // Ajouter une ligne de total
    const totalHours = monthPointages.reduce((sum, p) => sum + (parseFloat(p.heures_travaillees) || 0), 0);
    excelData.push({
      'Date': 'TOTAL',
      'Arrivée': '',
      'Départ': '',
      'Pause Début': '',
      'Pause Fin': '',
      'Heures Travaillées': totalHours.toFixed(2),
      'Note (pause)': ''
    });

    // Créer le fichier Excel
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pointages');

    // Télécharger le fichier
    const fileName = `pointages_${activeUser?.first_name || 'employe'}_${now.getFullYear()}_${(now.getMonth() + 1).toString().padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ==================== CRUD POINTAGES ====================
  const handleAddPointage = async (e) => {
    e.preventDefault();
    if (!canManagePointages()) return;

    setLoading(true);
    try {
      const targetUserId = selectedEmployeeId ? parseInt(selectedEmployeeId) : currentUser.employee_id;

      // Le backend recalcule total_hours + le détail de pause (source de vérité) à partir des
      // horaires et du planning du jour : on n'envoie plus heures_travaillees côté client.
      const response = await fetch(`${API_URL}/pointages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          user_id: targetUserId,
          date: pointageForm.date,
          heure_arrivee: pointageForm.heure_arrivee,
          heure_depart: pointageForm.heure_depart || null,
          pause_debut: pointageForm.pause_debut || null,
          pause_fin: pointageForm.pause_fin || null,
          statut: pointageForm.heure_depart ? 'termine' : 'en_cours',
          notes: pointageForm.notes
        })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'ajout');

      alert('Pointage ajouté avec succès');
      setShowAddModal(false);
      setPointageForm({
        date: new Date().toISOString().split('T')[0],
        heure_arrivee: '',
        heure_depart: '',
        pause_debut: '',
        pause_fin: '',
        notes: ''
      });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erreur ajout pointage:', error);
      alert('Erreur lors de l\'ajout du pointage');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPointage = async (e) => {
    e.preventDefault();
    if (!canManagePointages() || !editingPointage) return;

    setLoading(true);
    try {
      // Le backend recalcule total_hours + le détail de pause (source de vérité) à partir des
      // horaires et du planning du jour : on n'envoie plus heures_travaillees côté client.
      const response = await fetch(`${API_URL}/pointages/${editingPointage.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          heure_arrivee: pointageForm.heure_arrivee,
          heure_depart: pointageForm.heure_depart || null,
          pause_debut: pointageForm.pause_debut || null,
          pause_fin: pointageForm.pause_fin || null,
          statut: pointageForm.heure_depart ? 'termine' : 'en_cours',
          notes: pointageForm.notes
        })
      });

      if (!response.ok) throw new Error('Erreur lors de la modification');

      alert('Pointage modifié avec succès');
      setShowEditModal(false);
      setEditingPointage(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erreur modification pointage:', error);
      alert('Erreur lors de la modification du pointage');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePointage = async (pointageId) => {
    if (!canManagePointages()) return;
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce pointage ?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/pointages/${pointageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) throw new Error('Erreur lors de la suppression');

      alert('Pointage supprimé avec succès');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erreur suppression pointage:', error);
      alert('Erreur lors de la suppression du pointage');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (pointage) => {
    setEditingPointage(pointage);
    setPointageForm({
      date: pointage.date,
      heure_arrivee: pointage.heure_arrivee || '',
      heure_depart: pointage.heure_depart || '',
      pause_debut: pointage.pause_debut || '',
      pause_fin: pointage.pause_fin || '',
      notes: pointage.notes || ''
    });
    setShowEditModal(true);
  };

  // Filtrer les pointages pour l'utilisateur actif + filtre par mois
  const allUserPointages = pointages.filter(p => {
    if (p.user_id !== activeUserId) return false;
    if (ptgMonthFilter) {
      const [filterYear, filterMonth] = ptgMonthFilter.split('-').map(Number);
      const pDate = new Date(p.date);
      if (pDate.getFullYear() !== filterYear || pDate.getMonth() + 1 !== filterMonth) return false;
    }
    return true;
  }).sort((a, b) => new Date(b.date) - new Date(a.date));
  const ptgTotalPages = Math.max(1, Math.ceil(allUserPointages.length / PTG_PER_PAGE));
  const userPointages = allUserPointages.slice((ptgViewPage - 1) * PTG_PER_PAGE, ptgViewPage * PTG_PER_PAGE);

  // Navigation mois pour le filtre pointages
  const navigateMonth = (direction) => {
    const [y, m] = ptgMonthFilter.split('-').map(Number);
    const d = new Date(y, m - 1 + direction, 1);
    setPtgMonthFilter(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setPtgViewPage(1);
  };
  const monthFilterLabel = (() => {
    const [y, m] = ptgMonthFilter.split('-').map(Number);
    const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return `${monthNames[m - 1]} ${y}`;
  })();

  // Vérifier si un employé a un pointage en cours
  const hasActivePointage = (employeeId) => {
    const today = new Date().toISOString().split('T')[0];
    return pointages.some(p =>
      p.user_id === employeeId &&
      p.statut === 'en_cours' &&
      p.date === today
    );
  };

  const elapsedTime = currentPointage ? getElapsedTime(currentPointage) : null;
  const pauseTime = currentPointage ? getCurrentPauseTime(currentPointage) : null;

  // Filtrer les employés selon le rôle et les filtres actifs
  const getFilteredEmployees = () => {
    if (!authUser) return [];

    let filtered = [];

    // Patron et Super Manager voient tous les employés (Extras selon toggle)
    if (['Patron', 'Super Manager'].includes(authUser.role_name)) {
      filtered = showExtras
        ? employees
        : employees.filter(emp => emp.role_name !== 'Extra');
    }
    // Manager voit uniquement les employés de son service
    else if (authUser.role_name === 'Manager') {
      filtered = employees.filter(emp => emp.service_name === authUser.service_name);
    }

    // Appliquer la recherche texte
    if (searchFilter) {
      const s = searchFilter.toLowerCase();
      filtered = filtered.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(s) ||
        (emp.service_name || '').toLowerCase().includes(s)
      );
    }

    // Appliquer le filtre service
    if (serviceFilter) {
      filtered = filtered.filter(emp => emp.service_name === serviceFilter);
    }

    // Appliquer le filtre rôle
    if (roleFilter) {
      filtered = filtered.filter(emp => emp.role_name === roleFilter);
    }

    // S'assurer que l'utilisateur connecté est toujours en tête de liste
    const authUserEmployeeId = authUser.employee_id || authUser.id;
    const authUserInList = filtered.find(emp => emp.id === authUserEmployeeId);

    if (authUserInList) {
      filtered = [authUserInList, ...filtered.filter(emp => emp.id !== authUserEmployeeId)];
    } else if (currentUser && currentUser.id === authUserEmployeeId) {
      filtered = [currentUser, ...filtered];
    }

    return filtered;
  };

  const filteredEmployees = getFilteredEmployees();

  return (
    <div className="space-y-4 p-3 sm:p-0">
      {/* PATRON / SUPER MANAGER : grille de profils avec filtres */}
      {authUser && ['Patron', 'Super Manager'].includes(authUser.role_name) && (
        <div className="bg-surface rounded-xl shadow-lg p-3 sm:p-4 space-y-4">
          {/* Barre de recherche + bouton filtres + actions */}
          <div className="flex gap-2 items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchFilter}
                onChange={e => { setSearchFilter(e.target.value); setPtgViewPage(1); }}
                className="w-full pl-9 pr-3 py-2.5 min-h-[44px] border border-border-strong rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => setShowPtgFilters(!showPtgFilters)}
              className={`flex items-center justify-center gap-1.5 min-h-[44px] px-3 py-2 rounded-lg text-sm font-medium border transition shrink-0 ${
                (serviceFilter || roleFilter || showExtras) ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700' : 'bg-surface text-text-secondary border-border-strong hover:bg-elevated-1'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filtres</span>
              {(serviceFilter || roleFilter || showExtras) && (
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-600 text-white text-xs">
                  {[serviceFilter, roleFilter, showExtras].filter(Boolean).length}
                </span>
              )}
            </button>
            <button onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center gap-1.5 min-h-[44px] px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition shrink-0">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Ajouter</span>
            </button>
            <button onClick={handleExportExcel}
              className="flex items-center justify-center gap-1.5 min-h-[44px] px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition shrink-0">
              <Download className="w-4 h-4" /> <span className="hidden sm:inline">Excel</span>
            </button>
          </div>
          {/* Filtres collapsibles */}
          {showPtgFilters && (
            <div className="flex flex-wrap gap-2">
              <select value={serviceFilter} onChange={e => { setServiceFilter(e.target.value); setPtgViewPage(1); }}
                className="flex-1 min-w-[140px] min-h-[44px] px-3 py-2 border border-border-strong rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Tous les services</option>
                {[...new Set(employees.filter(e => e.service_name).map(e => e.service_name))].sort().map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPtgViewPage(1); }}
                className="flex-1 min-w-[140px] min-h-[44px] px-3 py-2 border border-border-strong rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Tous les rôles</option>
                {['Patron', 'Super Manager', 'Manager', 'Employé'].map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <button
                onClick={() => { setShowExtras(!showExtras); setPtgViewPage(1); }}
                className={`flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 rounded-lg text-sm font-medium border transition ${
                  showExtras
                    ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700'
                    : 'bg-elevated-1 text-text-secondary border-border-strong hover:bg-elevated-1'
                }`}
              >
                <Users className="w-4 h-4" />
                Extras {showExtras ? 'inclus' : 'exclus'}
              </button>
            </div>
          )}
          {/* Grille de cartes profil */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 max-h-48 sm:max-h-60 overflow-y-auto">
            {filteredEmployees.map(emp => {
              const rc = ROLE_COLORS[emp.role_name] || ROLE_COLORS['Employé'];
              const isActive = hasActivePointage(emp.id);
              const isSelected = parseInt(selectedEmployeeId) === emp.id;
              return (
                <button
                  key={emp.id}
                  onClick={() => setSelectedEmployeeId(emp.id)}
                  className={`relative p-2 sm:p-3 rounded-xl border-2 text-left transition hover:shadow-md flex flex-col items-center sm:items-start ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 shadow-md'
                      : 'border-border-subtle bg-surface hover:border-blue-300 dark:border-blue-700'
                  }`}
                >
                  <span className={`absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full ${
                    isActive ? 'bg-green-500 animate-pulse' : 'bg-gray-300'
                  }`} />
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br ${rc.avatar} flex items-center justify-center text-white font-bold text-xs sm:text-sm mb-1 sm:mb-2`}>
                    {(emp.first_name?.[0] || '') + (emp.last_name?.[0] || '')}
                  </div>
                  <p className="text-xs font-semibold text-text-primary truncate w-full text-center sm:text-left">
                    {emp.first_name} {emp.last_name}
                  </p>
                  <span className={`inline-block mt-0.5 sm:mt-1 px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium ${rc.bg} ${rc.text}`}>
                    {emp.role_name}
                  </span>
                  {emp.service_name && (
                    <p className="text-[10px] sm:text-xs text-text-muted truncate w-full text-center sm:text-left mt-0.5">{emp.service_name}</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* MANAGER / EMPLOYÉ / EXTRA : bandeau unifié modernisé */}
      {authUser && !['Patron', 'Super Manager'].includes(authUser.role_name) && (
        <div className="bg-surface rounded-2xl shadow-md border border-border-subtle p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3">
              {(() => {
                const rc = ROLE_COLORS[authUser.role_name] || ROLE_COLORS['Employé'];
                return (
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${rc.avatar} flex items-center justify-center text-white font-bold text-base shadow`}>
                    {(activeUser?.first_name?.[0] || '') + (activeUser?.last_name?.[0] || '')}
                  </div>
                );
              })()}
              <div>
                <p className="font-bold text-text-primary">{activeUser?.first_name} {activeUser?.last_name}</p>
                <div className="flex items-center gap-2 text-sm mt-0.5">
                  {(() => {
                    const rc = ROLE_COLORS[authUser.role_name] || ROLE_COLORS['Employé'];
                    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${rc.bg} ${rc.text}`}>{authUser.role_name}</span>;
                  })()}
                  {authUser.service_name && <span className="text-text-muted text-xs">{authUser.service_name}</span>}
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap w-full sm:w-auto">
              {authUser.role_name === 'Manager' && (
                <button onClick={() => setShowAddModal(true)}
                  className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 py-2 min-h-[44px] bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg text-sm transition border border-blue-200 dark:border-blue-800 font-medium">
                  <Plus className="w-4 h-4" /> Ajouter
                </button>
              )}
              <button onClick={handleExportExcel}
                className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 py-2 min-h-[44px] bg-elevated-1 hover:bg-elevated-1 text-text-secondary rounded-lg text-sm transition border border-border-subtle">
                <Download className="w-4 h-4" /> Excel
              </button>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openCongesModal'))}
                className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 py-2 min-h-[44px] bg-purple-50 dark:bg-purple-950/40 hover:bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-lg text-sm transition border border-purple-200 dark:border-purple-800 font-medium">
                <Calendar className="w-4 h-4" /> Demander un congé
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CARTE PRINCIPALE POINTAGE */}
      <div className="bg-surface rounded-2xl shadow-xl overflow-hidden border border-border-subtle">
        {/* Horloge premium — fond sombre avec glow */}
        <div className="relative overflow-hidden bg-gray-950 px-3 sm:px-6 py-6 sm:py-8 text-white">
          {/* Cercles décoratifs */}
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-indigo-600/10 blur-2xl pointer-events-none"></div>
          <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-blue-500/10 blur-xl pointer-events-none"></div>
          {/* En-tête */}
          <div className="relative flex items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-indigo-300" />
              </div>
              <div>
                <span className="font-bold text-base text-white">Pointeuse</span>
                {activeUser && parseInt(selectedEmployeeId) !== (currentUser?.employee_id || currentUser?.id) && (
                  <span className="text-text-muted text-sm ml-2">— {activeUser.first_name} {activeUser.last_name}</span>
                )}
              </div>
            </div>
            {currentPointage ? (
              <span className="flex items-center gap-1.5 px-3 py-1 bg-green-500/20 border border-green-500/30 text-green-400 rounded-full text-xs font-semibold">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                En cours
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-3 py-1 bg-gray-700/50 border border-gray-600/40 text-text-muted rounded-full text-xs font-semibold">
                <span className="w-1.5 h-1.5 bg-gray-500 rounded-full"></span>
                Non pointé
              </span>
            )}
          </div>
          {/* Horloge numérique */}
          <div className="relative text-center">
            <div className="text-5xl sm:text-7xl font-bold font-mono tracking-widest text-white tabular-nums" style={{ textShadow: '0 0 40px rgba(99,102,241,0.4)' }}>
              {currentTime.getHours().toString().padStart(2, '0')}
              <span className="text-indigo-400 animate-pulse">:</span>
              {currentTime.getMinutes().toString().padStart(2, '0')}
              <span className="text-indigo-400 animate-pulse">:</span>
              {currentTime.getSeconds().toString().padStart(2, '0')}
            </div>
            <p className="text-text-muted text-sm mt-2 capitalize font-medium">
              {currentTime.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="p-3 sm:p-6 space-y-4">
          {currentUser ? (
            <>
              {/* TIMER TEMPS RÉEL */}
              {currentPointage && elapsedTime && (
                <div className="p-3 sm:p-5 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Timer className="w-5 h-5 text-green-600 dark:text-green-400" />
                      <span className="font-semibold text-text-primary">Temps travaillé</span>
                    </div>
                    <span className="text-xs text-text-muted">Arrivée : {currentPointage.heure_arrivee}</span>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl sm:text-5xl font-bold text-green-700 dark:text-green-300 font-mono">
                      {String(elapsedTime.hours).padStart(2, '0')}:
                      {String(elapsedTime.minutes).padStart(2, '0')}:
                      {String(elapsedTime.seconds).padStart(2, '0')}
                    </div>
                  </div>
                  {/* TIMER PAUSE */}
                  {pauseTime && (
                    <div className="mt-4 p-3 bg-orange-100 dark:bg-orange-900/40 rounded-lg border border-orange-300">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Coffee className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                          <span className="font-medium text-orange-900 text-sm">En pause</span>
                        </div>
                        <div className="text-xl font-bold text-orange-600 dark:text-orange-400 font-mono">
                          {String(pauseTime.minutes).padStart(2, '0')}:{String(pauseTime.seconds).padStart(2, '0')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* BOUTONS ACTIONS */}
              <div className="space-y-3">
                {currentPointage ? (
                  <>
                    {!currentPointage.pause_debut ? (
                      <button
                        className="w-full min-h-[56px] py-4 px-4 border-2 border-orange-300 hover:bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 font-bold text-lg rounded-xl flex items-center justify-center gap-3 transition disabled:opacity-50"
                        onClick={handlePauseDebut}
                        disabled={loading}
                      >
                        <Coffee className="w-6 h-6" />
                        Partir en pause
                      </button>
                    ) : !currentPointage.pause_fin ? (
                      <button
                        className="w-full min-h-[56px] py-4 px-4 bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg rounded-xl flex items-center justify-center gap-3 transition disabled:opacity-50"
                        onClick={handlePauseFin}
                        disabled={loading}
                      >
                        <Play className="w-6 h-6" />
                        Reprendre le travail
                      </button>
                    ) : null}
                    <button
                      className="w-full min-h-[56px] py-4 px-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold text-lg rounded-xl flex items-center justify-center gap-3 transition shadow-md disabled:opacity-50"
                      onClick={handleDepart}
                      disabled={loading}
                    >
                      <LogOut className="w-6 h-6" />
                      Pointer le départ
                    </button>
                  </>
                ) : (
                  <button
                    className="w-full min-h-[56px] py-5 px-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-xl rounded-xl flex items-center justify-center gap-3 transition shadow-md disabled:opacity-50"
                    onClick={handleArrivee}
                    disabled={loading}
                  >
                    <LogIn className="w-7 h-7" />
                    Pointer l'arrivée
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="text-center text-text-muted py-8">
              Connectez-vous pour pointer
            </div>
          )}
        </div>
      </div>

      {/* STATISTIQUES HEBDOMADAIRES ET MENSUELLES */}
      {currentUser && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* SEMAINE */}
          <div className="bg-surface rounded-2xl shadow-md border border-border-subtle overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 px-3 sm:px-5 py-3">
              <h4 className="text-sm font-bold text-blue-800 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                Cette semaine
              </h4>
            </div>
            <div className="p-3 sm:p-5">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-text-muted text-sm">Heures travaillées</span>
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400 tabular-nums">
                    {weeklyStats.hours}h
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-muted text-sm">Jours pointés</span>
                  <span className="text-lg font-semibold text-text-secondary">
                    {weeklyStats.days} jour{weeklyStats.days > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="pt-2 border-t border-border-subtle">
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted text-xs">Moyenne/jour</span>
                    <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      {weeklyStats.days > 0
                        ? (weeklyStats.hours / weeklyStats.days).toFixed(1)
                        : '0'}h
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* MOIS */}
          <div className="bg-surface rounded-2xl shadow-md border border-border-subtle overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-violet-50 border-b border-purple-100 px-3 sm:px-5 py-3">
              <h4 className="text-sm font-bold text-purple-800 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                Ce mois-ci
              </h4>
            </div>
            <div className="p-3 sm:p-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary text-sm">Heures travaillées</span>
                  <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {monthlyStats.hours}h
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary text-sm">Heures restantes</span>
                  <span className={`text-lg font-semibold ${
                    parseFloat(monthlyStats.remainingHours) > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'
                  }`}>
                    {monthlyStats.remainingHours > 0
                      ? `${monthlyStats.remainingHours}h`
                      : 'Objectif atteint'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary text-sm">
                    {parseFloat(monthlyStats.extraHours) >= 0 ? 'En avance (prorata)' : 'En retard (prorata)'}
                  </span>
                  <span className={`text-lg font-semibold ${
                    parseFloat(monthlyStats.extraHours) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {parseFloat(monthlyStats.extraHours) >= 0 ? '+' : ''}{parseFloat(monthlyStats.extraHours).toFixed(2)}h
                  </span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-text-secondary text-xs">Moyenne/semaine</span>
                    <span className="text-sm font-medium text-text-secondary">
                      {monthlyStats.avgPerWeek}h
                    </span>
                  </div>
                </div>
                
                {/* Info explicative */}
                <div className="mt-4 pt-4 border-t border-border-subtle">
                  <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800 font-semibold mb-1">
                      ℹ️ Différence entre "En avance" et "Modulation"
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                      <strong>En avance (prorata)</strong> : {parseFloat(monthlyStats.extraHours).toFixed(1)}h = comparé au planning prévu à ce jour ({new Date().getDate()}/{new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate()} du mois).
                      <br />
                      <strong>Solde final (modulation)</strong> : {(parseFloat(monthlyStats.hours) - ((activeUser?.heures_hebdomadaires || 35) * 52 / 12)).toFixed(1)}h = heures à récupérer à la fin du mois complet.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ENCADRÉ MODULATION MENSUELLE */}
      {currentUser && activeUser?.heures_hebdomadaires && (
        <ModulationRecapCard activeUser={activeUser} monthlyStats={monthlyStats} />
      )}

      {/* TABLEAU DES POINTAGES */}
      {currentUser && (
        <div className="bg-surface rounded-xl shadow-lg overflow-hidden">
          <div className="border-b border-border-subtle px-3 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base sm:text-lg font-bold text-text-primary">
                Pointages
              {activeUser && parseInt(selectedEmployeeId) !== (currentUser?.employee_id || currentUser?.id) && (
                <span className="text-sm font-normal text-text-secondary ml-2">
                  — {activeUser.first_name} {activeUser.last_name}
                </span>
              )}
            </h3>
              <span className="text-sm text-text-muted">
                {allUserPointages.length} pointage{allUserPointages.length > 1 ? 's' : ''}
              </span>
            </div>
            {/* Filtre par mois */}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => navigateMonth(-1)}
                className="min-h-[36px] min-w-[36px] flex items-center justify-center rounded-lg border border-border-strong hover:bg-elevated-1 transition"
                title="Mois précédent"
              >
                <span className="text-text-secondary font-bold">←</span>
              </button>
              <span className="text-sm font-semibold text-text-secondary min-w-[140px] text-center">
                {monthFilterLabel}
              </span>
              <button
                onClick={() => navigateMonth(1)}
                className="min-h-[36px] min-w-[36px] flex items-center justify-center rounded-lg border border-border-strong hover:bg-elevated-1 transition"
                title="Mois suivant"
              >
                <span className="text-text-secondary font-bold">→</span>
              </button>
            </div>
          </div>

          {allUserPointages.length === 0 ? (
            <div className="px-6 py-8 text-center text-text-muted">
              Aucun pointage pour {monthFilterLabel}
            </div>
          ) : (
          <>
          {/* Cards mobile */}
          <div className="md:hidden space-y-2 p-3">
            {userPointages.map((pointage) => (
              <div key={pointage.id} className={`rounded-xl border p-3 ${
                pointage.statut === 'en_cours' ? 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800' : 'bg-surface border-border-subtle'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-text-primary text-sm">
                    {new Date(pointage.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    pointage.statut === 'termine'
                      ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                      : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                  }`}>
                    {pointage.statut === 'termine' ? 'Termine' : 'En cours'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1.5">
                    <LogIn className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    <span className="text-text-secondary">{pointage.heure_arrivee || '--'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <LogOut className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                    <span className="text-text-secondary">{pointage.heure_depart || '--'}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-border-subtle">
                  <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                    {pointage.heures_travaillees
                      ? `${parseFloat(pointage.heures_travaillees).toFixed(2)}h`
                      : '--'}
                  </span>
                  {canManagePointages() && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEditModal(pointage)}
                        className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 rounded-lg transition" title="Modifier">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeletePointage(pointage.id)}
                        className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-red-600 hover:bg-red-50 dark:bg-red-950/40 rounded-lg transition" title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Tableau desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-elevated-1 text-text-muted text-xs uppercase tracking-wide border-b border-border-subtle">
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Entree</th>
                  <th className="px-4 py-3 text-left">Sortie</th>
                  <th className="px-4 py-3 text-left">Pause</th>
                  <th className="px-4 py-3 text-right">Heures</th>
                  <th className="px-4 py-3 text-center">Statut</th>
                  {canManagePointages() && <th className="px-4 py-3 text-center">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {userPointages.map((pointage, idx) => (
                  <tr key={pointage.id} className={`hover:bg-elevated-1 transition ${
                    pointage.statut === 'en_cours' ? 'bg-green-50/60' : idx % 2 === 0 ? 'bg-surface' : 'bg-elevated-1/30'
                  }`}>
                    <td className="px-4 py-3 font-medium text-text-primary">
                      {new Date(pointage.date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      <div className="flex items-center gap-1">
                        <LogIn className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        {pointage.heure_arrivee || '--'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      <div className="flex items-center gap-1">
                        <LogOut className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                        {pointage.heure_depart || '--'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-muted text-xs">
                      {pointage.pause_debut
                        ? `${pointage.pause_debut} -> ${pointage.pause_fin || '...'}`
                        : '--'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {pointage.heures_travaillees
                        ? <span className="font-semibold text-blue-700 dark:text-blue-300">{parseFloat(pointage.heures_travaillees).toFixed(2)}h</span>
                        : '--'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        pointage.statut === 'termine'
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                      }`}>
                        {pointage.statut === 'termine' ? 'Termine' : 'En cours'}
                      </span>
                    </td>
                    {canManagePointages() && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => openEditModal(pointage)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 rounded transition" title="Modifier">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDeletePointage(pointage.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:bg-red-950/40 rounded transition" title="Supprimer">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {ptgTotalPages > 1 && (
            <div className="px-3 sm:px-6 py-3 border-t border-border-subtle flex items-center justify-between text-sm text-text-secondary">
              <span>Page {ptgViewPage} / {ptgTotalPages}</span>
              <div className="flex gap-2">
                <button
                  disabled={ptgViewPage === 1}
                  onClick={() => setPtgViewPage(p => p - 1)}
                  className="min-h-[44px] min-w-[44px] px-3 py-1 rounded border border-border-strong disabled:opacity-40 hover:bg-elevated-1 transition"
                >Precedent</button>
                <button
                  disabled={ptgViewPage === ptgTotalPages}
                  onClick={() => setPtgViewPage(p => p + 1)}
                  className="min-h-[44px] min-w-[44px] px-3 py-1 rounded border border-border-strong disabled:opacity-40 hover:bg-elevated-1 transition"
                >Suivant</button>
              </div>
            </div>
          )}
          </>
          )}
        </div>
      )}

      {/* MODAL AJOUT POINTAGE */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowAddModal(false)}>
          <div className="relative w-full sm:max-w-lg sm:mx-4 max-h-[90vh] sm:max-h-[85vh] bg-surface rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Barre de drag mobile */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>
            {/* Header sticky */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h2 className="text-lg font-semibold text-text-primary">Ajouter un pointage</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-muted hover:text-text-secondary rounded-full hover:bg-elevated-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Corps scrollable */}
            <form onSubmit={handleAddPointage} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={pointageForm.date}
                      onChange={(e) => setPointageForm({ ...pointageForm, date: e.target.value })}
                      required
                      className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Heure d'arrivée *
                    </label>
                    <input
                      type="time"
                      value={pointageForm.heure_arrivee}
                      onChange={(e) => setPointageForm({ ...pointageForm, heure_arrivee: e.target.value })}
                      required
                      className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Heure de départ
                    </label>
                    <input
                      type="time"
                      value={pointageForm.heure_depart}
                      onChange={(e) => setPointageForm({ ...pointageForm, heure_depart: e.target.value })}
                      className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Pause début
                    </label>
                    <input
                      type="time"
                      value={pointageForm.pause_debut}
                      onChange={(e) => setPointageForm({ ...pointageForm, pause_debut: e.target.value })}
                      className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Pause fin
                    </label>
                    <input
                      type="time"
                      value={pointageForm.pause_fin}
                      onChange={(e) => setPointageForm({ ...pointageForm, pause_fin: e.target.value })}
                      className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Notes
                  </label>
                  <textarea
                    value={pointageForm.notes}
                    onChange={(e) => setPointageForm({ ...pointageForm, notes: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Notes optionnelles..."
                  />
                </div>
              </div>

              {/* Footer sticky avec boutons */}
              <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium text-center"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 text-center"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL MODIFICATION POINTAGE */}
      {showEditModal && editingPointage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowEditModal(false)}>
          <div className="relative w-full sm:max-w-lg sm:mx-4 max-h-[90vh] sm:max-h-[85vh] bg-surface rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Barre de drag mobile */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>
            {/* Header sticky */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h2 className="text-lg font-semibold text-text-primary">Modifier le pointage</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-muted hover:text-text-secondary rounded-full hover:bg-elevated-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Corps scrollable */}
            <form onSubmit={handleEditPointage} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      value={pointageForm.date}
                      disabled
                      className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg bg-elevated-1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Heure d'arrivée *
                    </label>
                    <input
                      type="time"
                      value={pointageForm.heure_arrivee}
                      onChange={(e) => setPointageForm({ ...pointageForm, heure_arrivee: e.target.value })}
                      required
                      className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Heure de départ
                    </label>
                    <input
                      type="time"
                      value={pointageForm.heure_depart}
                      onChange={(e) => setPointageForm({ ...pointageForm, heure_depart: e.target.value })}
                      className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Pause début
                    </label>
                    <input
                      type="time"
                      value={pointageForm.pause_debut}
                      onChange={(e) => setPointageForm({ ...pointageForm, pause_debut: e.target.value })}
                      className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Pause fin
                    </label>
                    <input
                      type="time"
                      value={pointageForm.pause_fin}
                      onChange={(e) => setPointageForm({ ...pointageForm, pause_fin: e.target.value })}
                      className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Notes
                  </label>
                  <textarea
                    value={pointageForm.notes}
                    onChange={(e) => setPointageForm({ ...pointageForm, notes: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Notes optionnelles..."
                  />
                </div>
              </div>

              {/* Footer sticky avec boutons */}
              <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium text-center"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 text-center"
                >
                  Modifier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}